/**
 * BannerSlot — مكوّنات عرض البانرات (top strip · inline · slides · toast).
 * كلّها تستهلك useBannersByPlacement وتختار التصميم بحسب الـvariant.
 */
import * as React from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowRight, X } from "lucide-react";
import { useLocale } from "@/contexts/LocaleContext";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/lib/toast";
import {
  useBannersByPlacement,
  type BannersFilterCtx,
} from "@/hooks/useBannersByPlacement";
import type { BannerPlacement, BannerRow } from "@/utils/banner.types";
import { getBannerSizeClasses } from "@/lib/banner-sizes";
import { MobileCarousel } from "@/components/MobileCarousel";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useBannerTracking } from "@/lib/bannerTracking";
import { BannerDiagnosticOverlay } from "@/components/banners/BannerDiagnostic";
import { CopyLinkButton } from "@/components/CopyLinkButton";
import { PUBLIC_SITE_ORIGIN } from "@/lib/public-origin";

function bannerShareUrl(link: string | null | undefined): string {
  if (!link) return PUBLIC_SITE_ORIGIN;
  if (/^https?:\/\//i.test(link)) return link;
  return `${PUBLIC_SITE_ORIGIN}${link.startsWith("/") ? link : `/${link}`}`;
}




function pickText(ar: string, en: string, locale: string): string {
  if (locale === "ar") return ar?.trim() || en?.trim() || "";
  return en?.trim() || ar?.trim() || "";
}

interface SlotProps {
  placement: BannerPlacement;
  ctx?: BannersFilterCtx;
}

function CtaWrap({
  link,
  className,
  children,
  onClick,
}: {
  link: string;
  className: string;
  children: React.ReactNode;
  onClick?: () => void;
}) {
  if (!link) return <span className={className}>{children}</span>;
  const isExternal = /^https?:\/\//i.test(link);
  if (isExternal) {
    return (
      <a href={link} target="_blank" rel="noopener noreferrer" className={className} onClick={onClick}>
        {children}
      </a>
    );
  }
  return (
    <a href={link} className={className} onClick={onClick}>
      {children}
    </a>
  );
}

// ============= 1) Top Strip — شريط رفيع فوق الهيدر =============

const TOP_DISMISS_PREFIX = "lecmo-banner-dismissed-";

export function TopStripSlot() {
  const { items } = useBannersByPlacement("top_strip");
  const { locale } = useLocale();
  const tracking = useBannerTracking();
  const [dismissedIds, setDismissedIds] = React.useState<Set<string>>(new Set());
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  const itemsKey = items.map((b) => b.id).join(",");
  React.useEffect(() => {
    const next = new Set<string>();
    for (const b of items) {
      try {
        if (typeof window !== "undefined" && localStorage.getItem(TOP_DISMISS_PREFIX + b.id) === "1") {
          next.add(b.id);
        }
      } catch {
        /* ignore */
      }
    }
    setDismissedIds((prev) => {
      if (prev.size === next.size && [...prev].every((id) => next.has(id))) return prev;
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsKey]);

  const visible = items.filter((b) => !dismissedIds.has(b.id));
  const banner = visible[0];

  React.useEffect(() => {
    if (banner) void tracking.view(banner.id, banner.trigger_value);
  }, [banner, tracking]);

  // اضبط متغيّر CSS `--top-strip-h` ليتمكّن الهيدر والمحتوى من تعويض المسافة
  React.useEffect(() => {
    const root = typeof document !== "undefined" ? document.documentElement : null;
    if (!root) return;
    if (!banner) {
      root.style.setProperty("--top-strip-h", "0px");
      return;
    }
    const el = wrapperRef.current;
    const h = el ? el.getBoundingClientRect().height : 0;
    root.style.setProperty("--top-strip-h", `${Math.round(h)}px`);
    return () => {
      root.style.setProperty("--top-strip-h", "0px");
    };
  }, [banner, locale]);

  if (!banner) return null;

  const title = pickText(banner.title_ar, banner.title_en, locale);
  const body = pickText(banner.body_ar, banner.body_en, locale);
  const cta = pickText(banner.cta_label_ar, banner.cta_label_en, locale);

  const dismiss = () => {
    try {
      localStorage.setItem(TOP_DISMISS_PREFIX + banner.id, "1");
    } catch {
      /* ignore */
    }
    setDismissedIds(new Set([...dismissedIds, banner.id]));
  };

  const sz = getBannerSizeClasses("top_strip", banner.size);

  return (
    <div
      ref={wrapperRef}
      className="relative z-[60] w-full px-3 pt-2 md:px-0 md:pt-0"
      dir={locale === "ar" ? "rtl" : "ltr"}
    >
      <div className={`mx-auto flex max-w-7xl items-center gap-3 rounded-none bg-primary px-4 text-xs text-primary-foreground shadow-[0_4px_20px_rgba(0,0,0,0.08)] sm:text-[13px] md:rounded-none  ${sz.pad}`}>
        <div className="flex-1 truncate">
          {title && <span className="font-medium">{title}</span>}
          {title && body && <span className="mx-2 opacity-50">·</span>}
          {body && <span className="opacity-90">{body}</span>}
          {cta && banner.cta_link && (
            <CtaWrap
              link={banner.cta_link}
              className="ms-2 inline-flex items-center gap-1 underline-offset-2 hover:underline"
              onClick={() => void tracking.click(banner.id, banner.trigger_value)}
            >
              {cta} <ArrowRight className="h-3 w-3 rtl:-scale-x-100" />
            </CtaWrap>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label={locale === "ar" ? "إغلاق" : "Dismiss"}
          className="shrink-0 rounded p-1 opacity-70 hover:opacity-100"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

// ملاحظة: حُذف بانر «أوّل منتج في السلّة» (auto_cart_first_product) — التذكير
// بعد إضافة منتج للسلّة يكون رسالة واتساب/إيميل بعد ساعات/أيّام عبر
// `/admin/reminder-campaigns` تاب «السلّة المهجورة»، لا بانراً فوريّاً.

// ============= Story Banner — بطاقة أفقيّة مرتفعة تكبر عند النقر =============


function StoryBannerCard({
  banner,
  locale,
  inCarousel = false,
}: {
  banner: BannerRow;
  locale: string;
  inCarousel?: boolean;
}) {
  const tracking = useBannerTracking();
  const [open, setOpen] = React.useState(false);
  const cardRef = React.useRef<HTMLButtonElement>(null);
  const isAr = locale === "ar";
  const title = pickText(banner.title_ar, banner.title_en, locale);
  const body = pickText(banner.body_ar, banner.body_en, locale);
  const cta = pickText(banner.cta_label_ar, banner.cta_label_en, locale);

  const onOpen = () => {
    void tracking.click(banner.id, banner.trigger_value);
    setOpen(true);
  };


  return (
    <>
      <div className={`relative ${inCarousel ? "w-full" : "w-full px-4 py-4"}`} dir={isAr ? "rtl" : "ltr"}>
        <button
          ref={cardRef}
          type="button"
          onClick={onOpen}
          aria-label={isAr ? "افتح البانر" : "Open banner"}
          className={`group relative block w-full overflow-hidden rounded-none border border-border/60 bg-secondary/40  transition-transform active:scale-[0.98] ${
            inCarousel ? "aspect-[9/16]" : "aspect-[6/5]"
          }`}
        >
          <img
            src={banner.image_url!}
            alt={title || ""}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover"
          />
          {/* تدرّج سفلي لقراءة النصّ */}
          {(title || body) && (
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4 pt-12 text-start text-white">
              {title && <h3 className="text-lg font-semibold leading-tight">{title}</h3>}
              {body && <p className="mt-1 line-clamp-2 text-xs opacity-90">{body}</p>}
            </div>
          )}
          {/* أُزيلت أيقونة وشارة «اضغط للتكبير» — الصور كبيرة أصلاً والتكبير يُصغّرها */}
          <BannerDiagnosticOverlay
            targetRef={cardRef}
            label={`story · ${inCarousel ? "carousel" : "solo"} · ${banner.id.slice(0, 6)}`}
          />
        </button>
        {/* زرّ المشاركة — أسفل البطاقة لتفادي تداخله مع شارة «اضغط للتكبير» في الأعلى */}
        {banner.image_url && (
          <div className="absolute bottom-3 end-3 z-10 px-1">
            <div className="relative">
              <CopyLinkButton url={bannerShareUrl(banner.cta_link)} className="rounded-full bg-background/80 backdrop-blur" />
            </div>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-[95vw] border-0 bg-black p-0 sm:max-w-2xl"
          dir={isAr ? "rtl" : "ltr"}
        >
          <div className="relative w-full overflow-hidden rounded-none" style={{ aspectRatio: "6 / 5" }}>
            <img
              src={banner.image_url!}
              alt={title || ""}
              className="absolute inset-0 h-full w-full object-contain"
            />
            {(title || body || (cta && banner.cta_link)) && (
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-5 pt-16 text-start text-white">
                {title && <h2 className="text-xl font-semibold leading-tight">{title}</h2>}
                {body && <p className="mt-2 text-sm leading-relaxed opacity-95">{body}</p>}
                {cta && banner.cta_link && (
                  <CtaWrap
                    link={banner.cta_link}
                    className="mt-4 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-medium uppercase tracking-wider text-black transition-opacity hover:opacity-90"
                    onClick={() => {
                      void tracking.click(banner.id, banner.trigger_value);
                      setOpen(false);
                    }}
                  >
                    {cta} <ArrowRight className="h-3.5 w-3.5 rtl:-scale-x-100" />
                  </CtaWrap>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============= 2) Inline Banner — عام للهوم/المنتج/الشوب/الحساب =============


interface InlineSlotProps extends SlotProps {
  variant?: "card" | "wide";
}

function splitStoryBanners(items: BannerRow[]) {
  const stories = items.filter((b) => b.display_mode === "story" && b.image_url);
  const regular = items.filter((b) => !(b.display_mode === "story" && b.image_url));

  const sortGroup = (arr: BannerRow[]) =>
    [...arr].sort((a, b) => {
      const sa = a.sort_order ?? 0;
      const sb = b.sort_order ?? 0;
      if (sa !== sb) return sa - sb;
      return a.id.localeCompare(b.id);
    });

  // القاعدة: نلتزم بإعدادات الأدمن حرفياً.
  // - ستوري بنفس اسم الحملة (campaign) وعددها ≥ 2 → كاروسيل أفقي واحد.
  // - أيّ ستوري بدون campaign، أو حملة بشريحة واحدة → تُعرض منفردة كما ضبطها الأدمن.
  //   (لا تجميع تلقائيّ للستوري غير المسمّاة)
  const groupsMap = new Map<string, BannerRow[]>();
  const solos: BannerRow[] = [];
  for (const s of stories) {
    const key = s.campaign?.trim();
    if (!key) {
      solos.push(s);
      continue;
    }
    if (!groupsMap.has(key)) groupsMap.set(key, []);
    groupsMap.get(key)!.push(s);
  }

  const storyGroups: { key: string; items: BannerRow[] }[] = [];
  for (const [key, arr] of groupsMap) {
    if (arr.length >= 2) {
      storyGroups.push({ key, items: sortGroup(arr) });
    } else {
      solos.push(...arr);
    }
  }

  return { storySolos: sortGroup(solos), storyGroups, regular };

}


function StoryGroupCarousel({
  items,
  locale,
  groupKey,
}: {
  items: BannerRow[];
  locale: string;
  groupKey: string;
}) {
  const isAr = locale === "ar";
  const tracking = useBannerTracking();
  // كلّ شريحة تصبح نشطة تُسجَّل كـview (الـhook نفسه يمنع التكرار خلال الجلسة).
  // الشريحة الأولى تُسجَّل تلقائيّاً لأنّ Embla يستدعي onSelect فوراً عند mount.
  const onSlideChange = React.useCallback(
    (idx: number) => {
      const b = items[idx];
      if (b) void tracking.view(b.id, b.trigger_value);
    },
    [items, tracking],
  );
  return (
    <div className="w-full px-4 py-4">
      <MobileCarousel
        items={items}
        getKey={(b) => b.id}
        renderItem={(b) => <StoryBannerCard banner={b} locale={locale} inCarousel />}
        ariaPrefix={`${isAr ? "لوك" : "Look"}: ${groupKey}`}
        containerPadClass=""
        align="center"
        slideClassName="flex-[0_0_94%] sm:flex-[0_0_88%]"
        onSlideChange={onSlideChange}
      />
    </div>
  );
}

export function InlineBannerSlot({ placement, ctx, variant = "wide" }: InlineSlotProps) {
  const { items } = useBannersByPlacement(placement, ctx);
  const { locale } = useLocale();
  if (items.length === 0) return null;

  // الستوري على الموبايل: المنفردة تُكدَّس، والمجموعات (campaign مشترك) تُعرض ككاروسيل أفقي مرتفع.
  const { storySolos, storyGroups, regular: inlines } = splitStoryBanners(items);

  // الموبايل فقط: نفصل الستوري ونكدّسها بلا كاروسيل.
  // الديسكتوب: نُبقي السلوك الأصلي (كلّ العناصر داخل الكاروسيل/العرض الموحَّد).
  return (
    <section className="w-full">
      {/* === MOBILE === */}
      <div className="md:hidden">
        {storySolos.map((b) => (
          <InlineBannerCard key={b.id} banner={b} locale={locale} variant={variant} />
        ))}
        {storyGroups.map((g) => (
          <StoryGroupCarousel key={g.key} groupKey={g.key} items={g.items} locale={locale} />
        ))}
        {inlines.length === 1 && (
          <InlineBannerCard banner={inlines[0]} locale={locale} variant={variant} />
        )}
        {inlines.length > 1 && (
          <MobileCarousel
            items={inlines}
            getKey={(b) => b.id}
            renderItem={(b) => (
              <InlineBannerCard banner={b} locale={locale} variant={variant} inCarousel />
            )}
            ariaPrefix={locale === "ar" ? "بانر" : "banner"}
            containerPadClass=""
            align="center"
          />
        )}
      </div>

      {/* === DESKTOP — السلوك الأصلي قبل تعديلات الستوري === */}
      <div className="hidden md:block">
        {items.length === 1 && (
          <InlineBannerCard banner={items[0]} locale={locale} variant={variant} />
        )}
        {items.length > 1 && (
          <MobileCarousel
            items={items}
            getKey={(b) => b.id}
            renderItem={(b) => (
              <InlineBannerCard banner={b} locale={locale} variant={variant} inCarousel />
            )}
            ariaPrefix={locale === "ar" ? "بانر" : "banner"}
            containerPadClass=""
            align="center"
          />
        )}
      </div>
    </section>
  );
}

function InlineBannerCard({
  banner,
  locale,
  variant,
  inCarousel = false,
}: {
  banner: BannerRow;
  locale: string;
  variant: "card" | "wide";
  inCarousel?: boolean;
}) {
  const tracking = useBannerTracking();
  React.useEffect(() => {
    void tracking.view(banner.id, banner.trigger_value);
  }, [banner.id, banner.trigger_value, tracking]);

  // بطاقة الستوري عرض موبايل فقط؛ على الديسكتوب نتابع للعرض العاديّ.
  if (banner.display_mode === "story" && banner.image_url) {
    return (
      <div className="md:hidden">
        <StoryBannerCard banner={banner} locale={locale} />
      </div>
    );
  }



  const title = pickText(banner.title_ar, banner.title_en, locale);
  const body = pickText(banner.body_ar, banner.body_en, locale);
  const cta = pickText(banner.cta_label_ar, banner.cta_label_en, locale);
  const sz = getBannerSizeClasses(banner.placement, banner.size);
  const hasText = !!(title || body || (cta && banner.cta_link));
  const hasImage = !!banner.image_url;
  if (!hasText && !hasImage) return null;

  // في الكاروسيل: ارتفاع موحّد + flex عمودي لمنع قفز المحتوى بين الشرائح.
  const baseCls = inCarousel
    ? `h-full flex flex-col`
    : ``;
  const containerCls =
    variant === "wide"
      ? `w-full overflow-hidden border-y border-border/60 bg-background ${sz.margin} ${baseCls}`
      : `w-full overflow-hidden rounded-none border border-border/60 bg-background ${sz.margin} ${baseCls}`;

  const imageCls = inCarousel
    ? "block w-full flex-1 min-h-0 object-cover"
    : "block w-full h-auto object-cover aspect-[16/9] md:aspect-[21/9]";

  const imageEl = hasImage ? (
    <img
      src={banner.image_url!}
      alt={title || ""}
      loading="lazy"
      className={imageCls}
    />
  ) : null;

  const textEl = hasText ? (
    <div className={`bg-secondary/40 px-4 text-center ${sz.pad} ${inCarousel ? "shrink-0" : ""}`}>
      <div className="mx-auto max-w-2xl">
        {title && <h2 className="t-h2 leading-tight">{title}</h2>}
        {body && (
          <p className="mt-3 text-base leading-relaxed text-muted-foreground md:t-body-lg">{body}</p>
        )}
        {cta && banner.cta_link && (
          <CtaWrap
            link={banner.cta_link}
            className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[var(--tracking-wider)] text-primary transition-colors hover:text-foreground"
            onClick={() => void tracking.click(banner.id, banner.trigger_value)}
          >
            {cta} <ArrowRight className="h-4 w-4 rtl:-scale-x-100" />
          </CtaWrap>
        )}
      </div>
    </div>
  ) : null;

  // غلاف ارتفاع موحّد في الكاروسيل (الأطفال flex يملؤونه).
  const heightWrap = inCarousel ? "aspect-[16/10] md:aspect-[21/9]" : "";

  // صورة فقط بلا نصّ + رابط CTA → نلفّ الكلّ برابط واحد قابل للنقر
  if (hasImage && !hasText && banner.cta_link) {
    return (
      <div className="relative">
        <CtaWrap
          link={banner.cta_link}
          className={`${containerCls} ${heightWrap} block`}
          onClick={() => void tracking.click(banner.id, banner.trigger_value)}
        >
          {imageEl}
        </CtaWrap>
        <div className="absolute bottom-3 end-3 z-10">
          <CopyLinkButton url={bannerShareUrl(banner.cta_link)} className="rounded-full bg-background/80 backdrop-blur" />
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${containerCls} ${heightWrap}`}>
      {imageEl}
      {textEl}
      {hasImage && (
        <div className="absolute bottom-3 end-3 z-10">
          <CopyLinkButton url={bannerShareUrl(banner.cta_link)} className="rounded-full bg-background/80 backdrop-blur" />
        </div>
      )}
    </div>
  );
}

// ============= 3) Slides Carousel — لشرائح متعدّدة (الهوم/الحساب) =============

export function BannerSlidesSlot({ placement, ctx }: SlotProps) {
  const { items } = useBannersByPlacement(placement, ctx);
  const { locale } = useLocale();
  if (items.length === 0) return null;
  const { storySolos, storyGroups, regular: slides } = splitStoryBanners(items);

  return (
    <section className="border-b border-border/60 bg-background py-6 md:py-10">
      {/* الموبايل: ستوري منفردة مكدّسة + مجموعات ستوري ككاروسيل أفقي مرتفع + كاروسيل عام للباقي */}
      <div className="md:hidden">
        {storySolos.map((banner) => (
          <StoryBannerCard key={banner.id} banner={banner} locale={locale} />
        ))}
        {storyGroups.map((g) => (
          <StoryGroupCarousel key={g.key} groupKey={g.key} items={g.items} locale={locale} />
        ))}
        {slides.length > 0 && (
          <MobileCarousel
            items={slides}
            getKey={(s) => s.id}
            renderItem={(s) => <SlideCard banner={s} locale={locale} />}
            ariaPrefix={locale === "ar" ? "بانر" : "banner slide"}
            containerPadClass="px-4"
            align="center"
          />
        )}
      </div>

      {/* الديسكتوب: السلوك الأصليّ — كلّ العناصر داخل الكاروسيل */}
      <div className="hidden md:block">
        <MobileCarousel
          items={items}
          getKey={(s) => s.id}
          renderItem={(s) => <SlideCard banner={s} locale={locale} />}
          ariaPrefix={locale === "ar" ? "بانر" : "banner slide"}
          containerPadClass="px-4"
          align="center"
        />
      </div>
    </section>
  );
}

function SlideCard({ banner, locale }: { banner: BannerRow; locale: string }) {
  const tracking = useBannerTracking();
  React.useEffect(() => {
    void tracking.view(banner.id, banner.trigger_value);
  }, [banner.id, banner.trigger_value, tracking]);


  const title = pickText(banner.title_ar, banner.title_en, locale);
  const body = pickText(banner.body_ar, banner.body_en, locale);
  const cta = pickText(banner.cta_label_ar, banner.cta_label_en, locale);
  const sz = getBannerSizeClasses(banner.placement, banner.size);
  const hasText = !!(title || body);
  const hasImage = !!banner.image_url;
  if (!hasText && !hasImage && !cta) return null;

  return (
    <div className="h-full">
      <div className="relative overflow-hidden rounded-none border border-border/60 bg-background">
        {hasImage && (
          <img
            src={banner.image_url!}
            alt={title || ""}
            loading="lazy"
            className="block w-full h-auto object-cover aspect-[4/5] md:aspect-[16/9]"
          />
        )}
        {hasText && (
          <div className={`bg-secondary/40 px-6 text-center ${sz.pad}`}>
            <div className="mx-auto max-w-2xl">
              {title && (
                <h2 className="leading-tight md:t-h1 md:text-5xl text-2xl">{title}</h2>
              )}
              {(title && body) && <div className="gold-rule mx-auto mt-6 max-w-[5rem] md:mt-8 md:max-w-[6rem]" />}
              {body && (
                <p className="mt-6 text-base leading-relaxed text-muted-foreground md:mt-8 md:t-body-lg">
                  {body}
                </p>
              )}
            </div>
          </div>
        )}
        {hasImage && (
          <div className="absolute bottom-3 end-3 z-10">
            <CopyLinkButton url={bannerShareUrl(banner.cta_link)} className="rounded-full bg-background/80 backdrop-blur" />
          </div>
        )}
      </div>
      {cta && banner.cta_link && (
        <div className="mt-4 text-center">
          <CtaWrap
            link={banner.cta_link}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[var(--tracking-wider)] text-primary transition-colors hover:text-foreground"
            onClick={() => void tracking.click(banner.id, banner.trigger_value)}
          >
            {cta} <ArrowRight className="h-4 w-4 rtl:-scale-x-100" />
          </CtaWrap>
        </div>
      )}
    </div>
  );
}

// ============= 4) Anonymous Toast =============

const TOAST_SEEN_PREFIX = "lecmo-banner-toast-seen-";

export function AnonymousToastSlot() {
  const { items } = useBannersByPlacement("anonymous_toast");
  const { user, loading } = useAuth();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const tracking = useBannerTracking();
  const firedRef = React.useRef<Set<string>>(new Set());

  React.useEffect(() => {
    if (loading || user) return;
    const banner = items[0];
    if (!banner) return;
    if (firedRef.current.has(banner.id)) return;

    let seen = false;
    try {
      seen = localStorage.getItem(TOAST_SEEN_PREFIX + banner.id) === "1";
    } catch {
      /* ignore */
    }
    if (seen) return;

    const isAr = locale === "ar";
    const title = pickText(banner.title_ar, banner.title_en, locale) ||
      (isAr ? "أهلاً بك في Lecmo ✨" : "Welcome to Lecmo ✨");
    const body = pickText(banner.body_ar, banner.body_en, locale);
    const cta = pickText(banner.cta_label_ar, banner.cta_label_en, locale) ||
      (isAr ? "أنشئ حساباً" : "Sign up");
    const target = banner.cta_link || "/auth";

    const delayMs = Math.max(0, banner.delay_seconds * 1000);
    const timer = setTimeout(() => {
      firedRef.current.add(banner.id);
      try {
        localStorage.setItem(TOAST_SEEN_PREFIX + banner.id, "1");
      } catch {
        /* ignore */
      }
      toast(title, {
        description: body || undefined,
        duration: 9000,
        action: {
          label: cta,
          onClick: () => {
            if (/^https?:\/\//i.test(target)) {
              window.location.href = target;
            } else if (target.startsWith("/auth")) {
              navigate({ to: "/auth", search: { mode: "signup" as const, redirect: "/account" } });
            } else {
              window.location.href = target;
            }
          },
        },
      });
    }, delayMs);

    return () => clearTimeout(timer);
  }, [items, user, loading, locale, navigate]);

  return null;
}
